/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useCallback, useEffect } from 'react';
import api from '../utils/api';
import { toET, parseDateOnly } from '../utils/et';
import { format } from 'date-fns';
import openFile from '../utils/openFile';
import { printInvoiceById } from './Billing';

function money(v) { return '$' + (parseFloat(v || 0) || 0).toFixed(2); }

const INV_PILL = {
  paid: { bg: '#E1F5EE', color: '#0F6E56', label: 'PAID' },
  sent: { bg: '#FFF3E0', color: '#E65100', label: 'OPEN - UNPAID' },
  disputed: { bg: '#FFEBEE', color: '#C62828', label: 'DISPUTED' },
  draft: { bg: '#FAEEDA', color: '#854F0B', label: 'DRAFT - NOT SENT' },
  void: { bg: '#eee', color: '#666', label: 'VOID' },
};

function debounce(fn, ms) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); };
}

function Badge({ bg, color, label }) {
  return <span style={{ background: bg, color, fontSize: 10, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>{label}</span>;
}

export default function Patients() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [trips, setTrips] = useState([]);
  const [faxes, setFaxes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [billing, setBilling] = useState(null);
  const [openNotes, setOpenNotes] = useState({});
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  // Mobile: one pane at a time (list OR profile) instead of the fixed
  // 360px column squeeze (DISPATCH-SPEED LAW: phone is the primary viewport)
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 700px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 700px)');
    const fn = e => setIsMobile(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  const doSearch = useCallback(debounce(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/patients/search?q=${encodeURIComponent(q)}&limit=30`);
      setResults(res.data.patients || []);
    } catch (err) { setResults([]); }
    setSearching(false);
  }, 300), []);

  const handleSearch = (e) => {
    setQuery(e.target.value);
    doSearch(e.target.value);
  };

  const openProfile = async (patient) => {
    setLoadingProfile(true);
    setSelectedPatient(patient);
    if (isMobile) window.scrollTo(0, 0); // profile replaces the list on phones
    setTrips([]);
    setFaxes([]);
    setBilling(null);
    setEditing(false);
    try {
      const res = await api.get(`/patients/${patient.id}`);
      setSelectedPatient(res.data.patient);
      setTrips(res.data.trips || []);
      setFaxes(res.data.faxes || []);
      setDocuments(res.data.documents || []);
      setBilling(res.data.billing || null);
    } catch (err) {}
    setLoadingProfile(false);
  };

  // Silent re-pull after a billing action (mark paid, note) so the numbers
  // Erika reads back on the call are current.
  const refreshBilling = async () => {
    if (!selectedPatient) return;
    try {
      const res = await api.get(`/patients/${selectedPatient.id}`);
      setTrips(res.data.trips || []);
      setBilling(res.data.billing || null);
    } catch (err) {}
  };

  const markInvoicePaid = async (inv) => {
    const ref = window.prompt('Mark invoice ' + inv.invoice_number + ' (' + money(inv.grand_total) + ') as PAID.\nCheck number or payment reference (optional):', '');
    if (ref === null) return;
    try {
      await api.put(`/billing/invoices/${inv.id}`, { status: 'paid', paymentReference: ref || null });
      await refreshBilling();
    } catch (e) { alert('Could not mark the invoice paid.'); }
  };

  const addInvoiceNote = async (inv) => {
    const note = window.prompt('Add a note to invoice ' + inv.invoice_number + ' (internal, never printed):', '');
    if (note === null || !note.trim()) return;
    try {
      await api.post(`/billing/invoices/${inv.id}/note`, { note });
      await refreshBilling();
      setOpenNotes(o => ({ ...o, [inv.id]: true }));
    } catch (e) { alert('Could not save the note.'); }
  };

  const startEdit = () => {
    setEditing(true);
    setEditData({
      behavioralNotes: selectedPatient.behavioral_notes || '',
      typicallyBehavioralHealth: selectedPatient.typically_behavioral_health || false,
      typicallyRequiresWheelchair: selectedPatient.typically_requires_wheelchair || false,
      isVip: selectedPatient.is_vip || false,
      doNotTransport: selectedPatient.do_not_transport || false,
      requiresAdminReview: selectedPatient.requires_admin_review || false,
      mrn: selectedPatient.mrn || '',
      dob: selectedPatient.dob || '',
      phone: selectedPatient.phone || '',
      billingAddress: selectedPatient.billing_address || '',
      email: selectedPatient.email || '',
      invoicesByEmail: !!selectedPatient.invoices_by_email,
      poaName: selectedPatient.poa_name || '',
      poaRelationship: selectedPatient.poa_relationship || '',
      poaPhone: selectedPatient.poa_phone || '',
      poaEmail: selectedPatient.poa_email || '',
      poaAddress: selectedPatient.poa_address || '',
      billToPoa: !!selectedPatient.bill_to_poa,
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/patients/${selectedPatient.id}`, editData);
      // Refresh
      const res = await api.get(`/patients/${selectedPatient.id}`);
      setSelectedPatient(res.data.patient);
      setEditing(false);
    } catch (err) { alert('Save failed'); }
  };

  const showList = !isMobile || !selectedPatient;

  return (
    <div style={{ padding: isMobile ? 12 : 20, maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ color: '#185FA5', marginBottom: 4 }}>Patient Profiles</h2>
      {!isMobile && (
        <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
          Search patients by name, MRN, or DOB. View trip history and BH notes.
        </p>
      )}

      {/* Search bar (hidden on mobile while a profile is open) */}
      <div style={{ position: 'relative', marginBottom: isMobile ? 12 : 20, display: showList ? 'block' : 'none' }}>
        <input
          type="text"
          value={query}
          onChange={handleSearch}
          placeholder="Search by name, MRN, or DOB..."
          style={{
            width: '100%', padding: '12px 16px', fontSize: 15, borderRadius: 10,
            border: '1.5px solid #ddd', outline: 'none', boxSizing: 'border-box',
          }}
          autoFocus
        />
        {searching && <span style={{ position: 'absolute', right: 14, top: 14, fontSize: 12, color: '#999' }}>Searching...</span>}
      </div>

      <div style={{ display: 'flex', gap: isMobile ? 0 : 20 }}>
        {/* Results list */}
        <div style={{
          flex: isMobile ? '1 1 auto' : '0 0 360px',
          maxHeight: isMobile ? 'none' : 'calc(100vh - 200px)',
          overflowY: isMobile ? 'visible' : 'auto',
          display: showList ? 'block' : 'none',
        }}>
          {results.length === 0 && query.length >= 2 && !searching && (
            <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
              No patients found
              {/* New private payers booked from the dashboard now get a profile
                  automatically; this covers anyone else (Erika, 2026-10-09). */}
              <div style={{ marginTop: 10 }}>
                <button onClick={async () => {
                  const name = window.prompt('Create a patient profile. Enter the name as Last, First:', query);
                  if (!name || !name.trim()) return;
                  try {
                    const r = await api.post('/patients', { name: name.trim() });
                    const id = r.data.patient?.id;
                    if (id) { const res = await api.get(`/patients/${id}`); setSelectedPatient(res.data.patient); setBilling(res.data.billing || null); }
                  } catch (e) {
                    if (e.response?.status === 409 && e.response.data?.existingId) {
                      const res = await api.get(`/patients/${e.response.data.existingId}`); setSelectedPatient(res.data.patient); setBilling(res.data.billing || null);
                    } else alert(e.response?.data?.error || 'Could not create the profile');
                  }
                }} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #185FA5', background: '#fff', color: '#185FA5', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  + Create patient profile
                </button>
              </div>
            </div>
          )}
          {results.map(p => (
            <div key={p.id} onClick={() => openProfile(p)} style={{
              padding: '10px 14px', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
              background: selectedPatient?.id === p.id ? '#E6F1FB' : '#fff',
              border: `1px solid ${selectedPatient?.id === p.id ? '#185FA5' : '#eee'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</span>
                <span style={{ fontSize: 11, color: '#888' }}>{p.total_trips} trips</span>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                {p.mrn && <span style={{ fontSize: 11, color: '#555' }}>MRN: {p.mrn}</span>}
                {p.dob && <span style={{ fontSize: 11, color: '#555' }}>DOB: {p.dob}</span>}
                {p.typically_behavioral_health && <Badge bg="#EEEDFE" color="#3C3489" label="BH" />}
                {p.typically_requires_wheelchair && <Badge bg="#E6F1FB" color="#0C447C" label="WC" />}
                {p.do_not_transport && <Badge bg="#FFEBEE" color="#C62828" label="DNT" />}
                {p.is_vip && <Badge bg="#FFF8E1" color="#F57F17" label="VIP" />}
              </div>
              {p.last_trip_date && (
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Last: {format(parseDateOnly(p.last_trip_date), 'M/d/yyyy')} → {p.last_trip_destination?.substring(0, 30)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Profile detail */}
        {selectedPatient && (
          <div style={{
            flex: 1, background: '#fff', borderRadius: 12, border: '1px solid #eee',
            padding: isMobile ? 14 : 20, minWidth: 0,
            maxHeight: isMobile ? 'none' : 'calc(100vh - 200px)',
            overflowY: isMobile ? 'visible' : 'auto',
          }}>
            {isMobile && (
              <button onClick={() => setSelectedPatient(null)} style={{
                marginBottom: 12, padding: '10px 16px', borderRadius: 8, border: '1px solid #185FA5',
                background: '#fff', color: '#185FA5', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}>← Back to results</button>
            )}
            {loadingProfile ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 18, color: '#333' }}>{selectedPatient.name}</h3>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                      {selectedPatient.mrn && <span style={{ fontSize: 12, color: '#555' }}>MRN: {selectedPatient.mrn}</span>}
                      {selectedPatient.dob && <span style={{ fontSize: 12, color: '#555' }}>DOB: {selectedPatient.dob}</span>}
                      {selectedPatient.phone && <span style={{ fontSize: 12, color: '#555' }}>Phone: {selectedPatient.phone}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {selectedPatient.typically_behavioral_health && <Badge bg="#EEEDFE" color="#3C3489" label="Behavioral Health" />}
                      {selectedPatient.typically_requires_wheelchair && <Badge bg="#E6F1FB" color="#0C447C" label="Wheelchair" />}
                      {selectedPatient.do_not_transport && <Badge bg="#FFEBEE" color="#C62828" label="DO NOT TRANSPORT" />}
                      {selectedPatient.requires_admin_review && <Badge bg="#FFF3E0" color="#E65100" label="Admin Review" />}
                      {selectedPatient.is_vip && <Badge bg="#FFF8E1" color="#F57F17" label="VIP" />}
                      {selectedPatient.autopay_enabled && <Badge bg="#EDF4FB" color="#185FA5"
                        label={'Auto-Pay ' + (selectedPatient.autopay_method === 'ach' ? 'bank' : 'card') + (selectedPatient.autopay_last4 ? ' ...' + selectedPatient.autopay_last4 : '')} />}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={async () => {
                      // B-119: magic-link customer portal. Email optional --
                      // blank = just copy the link to read over the phone.
                      const email = window.prompt('Portal link for ' + selectedPatient.name + '.\nEnter their email to send it, or leave blank to just copy the link:', selectedPatient.email || '');
                      if (email === null) return;
                      try {
                        const r = await api.post('/customer-portal/links', {
                          customerName: selectedPatient.name,
                          email: email || null,
                          sendEmail: !!email,
                        });
                        await navigator.clipboard.writeText(r.data.url).catch(() => {});
                        alert('Portal link created' + (r.data.emailed ? ' and emailed' : '') + '.\nLink copied to clipboard (expires in 90 days).');
                      } catch (e) { alert('Could not create portal link.'); }
                    }} style={{
                      padding: isMobile ? '10px 16px' : '6px 14px', borderRadius: 6, border: '1px solid #185FA5',
                      fontSize: isMobile ? 13 : 12,
                      fontWeight: 600, cursor: 'pointer', background: '#fff', color: '#185FA5',
                    }}>Portal link</button>
                    <button onClick={async () => {
                      // Auto-pay setup link (2026-08-25): payer saves a card or
                      // bank account in Helcim's vault; invoices then announce
                      // and auto-run the charge a day after emailing.
                      const email = window.prompt('Auto-Pay setup link for ' + selectedPatient.name + '.\nEnter the payer\'s email to send it, or leave blank to just copy the link:', '');
                      if (email === null) return;
                      try {
                        const r = await api.post('/autopay/links', {
                          patientName: selectedPatient.name,
                          email: email || null,
                        });
                        await navigator.clipboard.writeText(r.data.url).catch(() => {});
                        alert('Auto-Pay setup link created' + (r.data.emailed ? ' and emailed' : '') + '.\nLink copied to clipboard (works for 7 days).');
                      } catch (e) { alert('Could not create the Auto-Pay link.'); }
                    }} style={{
                      padding: isMobile ? '10px 16px' : '6px 14px', borderRadius: 6, border: '1px solid #5B3E96',
                      fontSize: isMobile ? 13 : 12,
                      fontWeight: 600, cursor: 'pointer', background: '#fff', color: '#5B3E96',
                    }}>Auto-Pay link</button>
                    <button onClick={editing ? saveEdit : startEdit} style={{
                      padding: isMobile ? '10px 16px' : '6px 14px', borderRadius: 6, border: 'none',
                      fontSize: isMobile ? 13 : 12,
                      fontWeight: 600, cursor: 'pointer',
                      background: editing ? '#0F6E56' : '#185FA5', color: '#fff',
                    }}>{editing ? 'Save' : 'Edit'}</button>
                  </div>
                </div>

                {/* Edit form */}
                {editing && (
                  <div style={{ background: '#f9f9f9', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13 }}>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>MRN</label>
                      <input value={editData.mrn} onChange={e => setEditData({...editData, mrn: e.target.value})}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>DOB</label>
                      <input value={editData.dob} onChange={e => setEditData({...editData, dob: e.target.value})}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    {/* Patient's own contact block (Paula 2026-10-15) */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Phone</label>
                        <input value={editData.phone} onChange={e => setEditData({...editData, phone: e.target.value})} inputMode="tel"
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Email</label>
                        <input value={editData.email} onChange={e => setEditData({...editData, email: e.target.value})} inputMode="email"
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Mailing / billing address (prints as Bill To on invoices)</label>
                      <textarea value={editData.billingAddress} onChange={e => setEditData({...editData, billingAddress: e.target.value})} rows={2}
                        placeholder={'Street\nCity, State ZIP'}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <input type="checkbox" checked={editData.invoicesByEmail} onChange={e => setEditData({...editData, invoicesByEmail: e.target.checked})} />
                        Email invoices to this address instead of mailing
                      </label>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>BH Notes</label>
                      <textarea value={editData.behavioralNotes} onChange={e => setEditData({...editData, behavioralNotes: e.target.value})}
                        rows={3} style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={editData.typicallyBehavioralHealth} onChange={e => setEditData({...editData, typicallyBehavioralHealth: e.target.checked})} /> BH Patient
                      </label>
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={editData.typicallyRequiresWheelchair} onChange={e => setEditData({...editData, typicallyRequiresWheelchair: e.target.checked})} /> Wheelchair
                      </label>
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={editData.doNotTransport} onChange={e => setEditData({...editData, doNotTransport: e.target.checked})} /> Do Not Transport
                      </label>
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={editData.requiresAdminReview} onChange={e => setEditData({...editData, requiresAdminReview: e.target.checked})} /> Requires Review
                      </label>
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={editData.isVip} onChange={e => setEditData({...editData, isVip: e.target.checked})} /> VIP
                      </label>
                    </div>
                    {/* POA / bill-to contact (Erika 2026-10-02): the person who
                        holds power of attorney and, if ticked, gets the bills.
                        Ticking "Send bills to POA" rewrites the invoice Bill To
                        and invoice email on save. */}
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e5e5' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#185FA5', marginBottom: 8 }}>Power of Attorney / Bill-to contact</div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>POA name</label>
                          <input value={editData.poaName} onChange={e => setEditData({...editData, poaName: e.target.value})} placeholder="Full name"
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Relationship</label>
                          <input value={editData.poaRelationship} onChange={e => setEditData({...editData, poaRelationship: e.target.value})} placeholder="daughter, son, attorney..."
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>POA phone</label>
                          <input value={editData.poaPhone} onChange={e => setEditData({...editData, poaPhone: e.target.value})} inputMode="tel"
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>POA email</label>
                          <input value={editData.poaEmail} onChange={e => setEditData({...editData, poaEmail: e.target.value})} inputMode="email"
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 2 }}>POA mailing address</label>
                        <textarea value={editData.poaAddress} onChange={e => setEditData({...editData, poaAddress: e.target.value})} rows={2}
                          placeholder={'Street\nCity, State ZIP'}
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
                      </div>
                      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#0F6E56' }}>
                        <input type="checkbox" checked={editData.billToPoa} onChange={e => setEditData({...editData, billToPoa: e.target.checked})} />
                        Send bills to the POA (invoices print and email to this person instead of the patient)
                      </label>
                    </div>
                    <button onClick={() => setEditing(false)} style={{ marginTop: 8, padding: '4px 10px', fontSize: 11, border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>Cancel</button>
                  </div>
                )}

                {/* BH Notes (always visible if set) */}
                {!editing && selectedPatient.behavioral_notes && (
                  <div style={{ background: '#EEEDFE', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                    <div style={{ fontWeight: 600, fontSize: 11, color: '#3C3489', marginBottom: 4 }}>Behavioral Health Notes</div>
                    <div style={{ whiteSpace: 'pre-wrap', color: '#333' }}>{selectedPatient.behavioral_notes}</div>
                  </div>
                )}

                {!editing && (selectedPatient.billing_address || selectedPatient.email) && (
                  <div style={{ background: '#F4F4F2', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                    <div style={{ fontWeight: 600, fontSize: 11, color: '#666', marginBottom: 4 }}>Contact{selectedPatient.bill_to_poa ? '' : ' / Bill To'}</div>
                    {selectedPatient.billing_address && <div style={{ color: '#333', whiteSpace: 'pre-wrap' }}>{selectedPatient.billing_address}</div>}
                    {selectedPatient.email && <div style={{ color: '#555' }}>Email: {selectedPatient.email}{selectedPatient.invoices_by_email ? ' (invoices by email)' : ''}</div>}
                  </div>
                )}

                {/* POA / bill-to contact (read view) */}
                {!editing && (selectedPatient.poa_name || selectedPatient.bill_to_poa) && (
                  <div style={{ background: selectedPatient.bill_to_poa ? '#E1F5EE' : '#F4F4F2', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                    <div style={{ fontWeight: 600, fontSize: 11, color: '#0F6E56', marginBottom: 4 }}>
                      Power of Attorney{selectedPatient.bill_to_poa ? ' - BILLS GO HERE' : ''}
                    </div>
                    <div style={{ color: '#333' }}>
                      <strong>{selectedPatient.poa_name || '(name not entered)'}</strong>
                      {selectedPatient.poa_relationship ? ' (' + selectedPatient.poa_relationship + ')' : ''}
                    </div>
                    {selectedPatient.poa_phone && <div style={{ color: '#555' }}>Phone: {selectedPatient.poa_phone}</div>}
                    {selectedPatient.poa_email && <div style={{ color: '#555' }}>Email: {selectedPatient.poa_email}</div>}
                    {selectedPatient.poa_address && <div style={{ color: '#555', whiteSpace: 'pre-wrap' }}>{selectedPatient.poa_address}</div>}
                    {selectedPatient.bill_to_poa && !selectedPatient.poa_address && !selectedPatient.poa_email && (
                      <div style={{ color: '#A32D2D', marginTop: 4 }}>No mailing address or email entered - bills have nowhere to go yet.</div>
                    )}
                  </div>
                )}

                {/* Billing (2026-09-24, Barner call): everything Erika needs to
                    answer "what do I owe / did you get my check" from the
                    profile. Facility-billed trips (LGH etc.) are tagged on
                    the trip row but never counted in this person's balance. */}
                {billing && (
                  <div style={{ marginBottom: 20 }}>
                    <h4 style={{ fontSize: 14, color: '#185FA5', marginBottom: 8 }}>Billing</h4>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      {[
                        { label: 'Owes now', value: billing.summary.open_balance, sub: billing.summary.open_count + ' open', bg: billing.summary.open_balance > 0 ? '#FFF3E0' : '#E1F5EE', color: billing.summary.open_balance > 0 ? '#E65100' : '#0F6E56' },
                        { label: 'Paid', value: billing.summary.paid_total, sub: billing.summary.paid_count + ' invoice' + (billing.summary.paid_count === 1 ? '' : 's'), bg: '#E1F5EE', color: '#0F6E56' },
                        { label: 'Not yet invoiced', value: billing.summary.unbilled_total + billing.summary.draft_total, sub: (billing.summary.unbilled_count + billing.summary.draft_count) + ' item' + ((billing.summary.unbilled_count + billing.summary.draft_count) === 1 ? '' : 's'), bg: '#f4f4f4', color: '#555' },
                      ].map(tile => (
                        <div key={tile.label} style={{ flex: '1 1 110px', background: tile.bg, borderRadius: 8, padding: '8px 12px' }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: tile.color, textTransform: 'uppercase' }}>{tile.label}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#222' }}>{money(tile.value)}</div>
                          <div style={{ fontSize: 11, color: '#777' }}>{tile.sub}</div>
                        </div>
                      ))}
                    </div>

                    {(selectedPatient.billing_address || selectedPatient.email || selectedPatient.caregiver_name) && (
                      <div style={{ fontSize: 12, color: '#555', background: '#f9f9f9', border: '1px solid #eee', borderRadius: 6, padding: '8px 12px', marginBottom: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {selectedPatient.billing_address && (
                          <div><div style={{ fontSize: 10, fontWeight: 600, color: '#888' }}>BILLS GO TO</div><div style={{ whiteSpace: 'pre-wrap' }}>{selectedPatient.billing_address}</div></div>
                        )}
                        {selectedPatient.email && (
                          <div><div style={{ fontSize: 10, fontWeight: 600, color: '#888' }}>EMAIL{selectedPatient.invoices_by_email ? ' (gets invoices by email)' : ''}</div><div>{selectedPatient.email}</div></div>
                        )}
                        {selectedPatient.caregiver_name && (
                          <div><div style={{ fontSize: 10, fontWeight: 600, color: '#888' }}>CAREGIVER</div><div>{selectedPatient.caregiver_name}{selectedPatient.caregiver_phone ? ' ' + selectedPatient.caregiver_phone : ''}</div></div>
                        )}
                      </div>
                    )}

                    {billing.invoices.length === 0 ? (
                      <div style={{ color: '#999', fontSize: 13, padding: 12 }}>No invoices in Rosie for this patient</div>
                    ) : billing.invoices.map(inv => {
                      const pill = INV_PILL[inv.status] || { bg: '#eee', color: '#555', label: String(inv.status || '').toUpperCase() };
                      const first = inv.first_trip ? format(toET(inv.first_trip), 'M/d/yy') : null;
                      const last = inv.last_trip ? format(toET(inv.last_trip), 'M/d/yy') : null;
                      const btn = (label, onClick, color) => (
                        <button onClick={onClick} style={{
                          padding: isMobile ? '9px 12px' : '5px 10px', borderRadius: 6, fontSize: isMobile ? 13 : 11, fontWeight: 600,
                          border: '1px solid ' + color, background: '#fff', color, cursor: 'pointer',
                        }}>{label}</button>
                      );
                      return (
                        <div key={inv.id} style={{ padding: '10px 12px', borderRadius: 6, marginBottom: 6, background: '#fff', border: '1px solid ' + (inv.status === 'sent' ? '#F5C89A' : '#eee'), fontSize: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                            <div>
                              <span style={{ fontWeight: 700, fontSize: 14, color: '#185FA5' }}>Invoice {inv.invoice_number}</span>
                              <span style={{ marginLeft: 10, fontWeight: 700, fontSize: 14 }}>{money(inv.grand_total)}</span>
                              <span style={{ marginLeft: 8, color: '#888' }}>{inv.line_count} trip{inv.line_count === '1' ? '' : 's'}{first ? ' ' + first + (last && last !== first ? ' - ' + last : '') : ''}</span>
                            </div>
                            <Badge bg={pill.bg} color={pill.color} label={pill.label} />
                          </div>
                          <div style={{ color: '#666', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {inv.sent_at && <span>Sent {format(toET(inv.sent_at), 'M/d/yyyy')}</span>}
                            {inv.paid_at && <span style={{ color: '#0F6E56', fontWeight: 600 }}>Paid {format(toET(inv.paid_at), 'M/d/yyyy')}{inv.payment_reference ? ' (' + inv.payment_reference + ')' : ''}</span>}
                            {!inv.sent_at && !inv.paid_at && inv.created_at && <span>Created {format(toET(inv.created_at), 'M/d/yyyy')}</span>}
                            {inv.autopay_charge_at && !inv.paid_at && <span style={{ color: '#5B3E96' }}>Auto-pay {inv.autopay_hold ? 'ON HOLD' : 'charges ' + format(toET(inv.autopay_charge_at), 'M/d')}</span>}
                            {inv.autopay_result && !inv.paid_at && <span style={{ color: '#C62828' }}>Auto-pay: {inv.autopay_result}</span>}
                          </div>
                          {inv.billing_address && (
                            <div style={{ color: '#888', marginTop: 4, fontSize: 11 }}>Mailed to: {inv.billing_address.replace(/\n/g, ', ')}</div>
                          )}
                          {inv.notes && (
                            <div style={{ marginTop: 6 }}>
                              <a href="#" onClick={e => { e.preventDefault(); setOpenNotes(o => ({ ...o, [inv.id]: !o[inv.id] })); }}
                                style={{ fontSize: 11, color: '#185FA5' }}>{openNotes[inv.id] ? 'Hide notes' : 'Show notes (' + inv.notes.split(' | ').length + ')'}</a>
                              {openNotes[inv.id] && (
                                <div style={{ background: '#FFFBE6', border: '1px solid #F5E6A3', borderRadius: 6, padding: '6px 10px', marginTop: 4, fontSize: 12, color: '#444' }}>
                                  {inv.notes.split(' | ').map((n, i) => <div key={i} style={{ marginBottom: 4 }}>{n}</div>)}
                                </div>
                              )}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                            {btn('View', () => printInvoiceById(inv.id, inv.facility_name, 'view'), '#185FA5')}
                            {(inv.status === 'sent' || inv.status === 'disputed') && btn('Mark paid', () => markInvoicePaid(inv), '#0F6E56')}
                            {btn('Add note', () => addInvoiceNote(inv), '#854F0B')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Trip History */}
                <h4 style={{ fontSize: 14, color: '#185FA5', marginBottom: 8 }}>
                  Trip History ({trips.length})
                </h4>
                {trips.length === 0 ? (
                  <div style={{ color: '#999', fontSize: 13, padding: 12 }}>No trips found in Rosie yet</div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    {trips.map(t => (
                      <div key={t.id} style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 4, background: '#f9f9f9', border: '1px solid #eee' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontWeight: 600, color: '#185FA5' }}>{t.transport_number}</span>
                            <span style={{ marginLeft: 8, color: '#888' }}>
                              {t.scheduled_time ? format(toET(t.scheduled_time), 'M/d/yyyy h:mm a') : ''}
                            </span>
                          </div>
                          <span style={{
                            fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                            background: t.status === 'completed' ? '#E8F5E9' : t.status === 'cancelled' ? '#FFEBEE' : '#FFF3E0',
                            color: t.status === 'completed' ? '#2E7D32' : t.status === 'cancelled' ? '#C62828' : '#E65100',
                          }}>{t.status}</span>
                        </div>
                        <div style={{ color: '#555', marginTop: 2 }}>
                          {t.origin_address?.substring(0, 35)} → {t.destination_address?.substring(0, 35)}
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 2, color: '#888' }}>
                          {t.patient_type && <span>{t.patient_type}</span>}
                          {t.requires_wheelchair && <span>WC</span>}
                          <span>{t.request_channel}</span>
                          {t.drivers && typeof t.drivers === 'object' && t.drivers.length > 0 && (
                            <span>{t.drivers.map(d => d.name).join(', ')}</span>
                          )}
                        </div>
                        {t.billing && (
                          <div style={{ marginTop: 4, fontSize: 11 }}>
                            {t.billing.invoice_number ? (
                              <span style={{ color: t.billing.invoice_status === 'paid' ? '#0F6E56' : '#E65100', fontWeight: 600 }}>
                                {money(t.billing.total)} on invoice {t.billing.invoice_number}
                                {t.billing.bill_to_type !== 'patient' && t.billing.billing_facility !== 'Private' ? ' (billed to ' + t.billing.billing_facility + ')' : ''}
                                {' - ' + (t.billing.invoice_status === 'paid' ? 'paid' : t.billing.invoice_status === 'sent' ? 'unpaid' : t.billing.invoice_status)}
                              </span>
                            ) : (
                              <span style={{ color: '#888' }}>
                                {money(t.billing.total)} {t.billing.bill_to_type === 'patient' || t.billing.billing_facility === 'Private' ? 'not yet invoiced' : 'billed to ' + t.billing.billing_facility + ', not yet invoiced'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Profile documents: facesheets etc. attached to the patient, not a trip (2026-10-09) */}
                <h4 style={{ fontSize: 14, color: '#185FA5', marginBottom: 8, marginTop: 20 }}>
                  Documents ({documents.length})
                </h4>
                {documents.length === 0 ? (
                  <div style={{ color: '#999', fontSize: 13, padding: 12 }}>No documents on this profile yet (facesheets from Willow Valley land here).</div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    {documents.map(f => (
                      <div key={f.id} style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 4, background: '#f9f9f9', border: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          <span style={{ fontWeight: 600, color: '#185FA5', textTransform: 'capitalize' }}>{String(f.doc_type || '').replace('_', ' ')}</span>
                          <span style={{ marginLeft: 8, color: '#555' }}>{f.original_filename}</span>
                          <span style={{ marginLeft: 8, color: '#888', fontSize: 11 }}>{f.created_at ? format(toET(f.created_at), 'M/d/yyyy h:mm a') : ''}</span>
                          {f.source?.facility && <span style={{ marginLeft: 8, color: '#aaa', fontSize: 11 }}>{f.source.facility}</span>}
                        </div>
                        <a href="#" onClick={(e) => openFile(f.id, e)}
                          style={{ padding: '4px 10px', borderRadius: 4, background: '#185FA5', color: '#fff', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {/* Fax Documents */}
                <h4 style={{ fontSize: 14, color: '#185FA5', marginBottom: 8, marginTop: 20 }}>
                  Fax Documents ({faxes.length})
                </h4>
                {faxes.length === 0 ? (
                  <div style={{ color: '#999', fontSize: 13, padding: 12 }}>No faxes attached</div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    {faxes.map(f => (
                      <div key={f.id} style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 4, background: '#f9f9f9', border: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          <span style={{ fontWeight: 600, color: '#185FA5' }}>{f.transport_number}</span>
                          <span style={{ marginLeft: 8, color: '#555' }}>{f.original_filename}</span>
                          <span style={{ marginLeft: 8, color: '#888', fontSize: 11 }}>
                            {f.created_at ? format(toET(f.created_at), 'M/d/yyyy h:mm a') : ''}
                          </span>
                          {f.file_size_bytes && (
                            <span style={{ marginLeft: 8, color: '#aaa', fontSize: 11 }}>
                              {(f.file_size_bytes / 1024).toFixed(0)} KB
                            </span>
                          )}
                        </div>
                        <a href="#" onClick={(e) => openFile(f.id, e)}
                          style={{ padding: '4px 10px', borderRadius: 4, background: '#185FA5', color: '#fff', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                          View
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
